import html2pdf from "html2pdf.js";
import { formatKolkataDateTime } from "./dateTime";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function downloadBlob(content, fileName, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function toExportRows(rows, dateField) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => ({
    sl: index + 1,
    franchise: row?.centre_name || row?.login_id || "-",
    centreCode: row?.centre_code || "-",
    contact: row?.contact || "-",
    amount: Number(row?.amount || 0).toFixed(2),
    paymentMode: String(row?.payment_mode || "-").replaceAll("_", " "),
    reference: row?.reference_no || "-",
    franchiseNote: row?.note || "-",
    adminNote: row?.admin_note || "-",
    date: formatKolkataDateTime(row?.[dateField]) || "-",
  }));
}

export function exportLoadMoneyCsv({ rows, fileName, dateField = "updated_at", dateLabel = "Date" }) {
  const exportRows = toExportRows(rows, dateField);
  const header = [
    "Sl No",
    "Franchise",
    "Centre Code",
    "Contact",
    "Amount (Rs)",
    "Payment Mode",
    "Reference",
    "Franchise Note",
    "Admin Note",
    dateLabel,
  ];

  const lines = [
    header,
    ...exportRows.map((row) => [
      row.sl,
      row.franchise,
      row.centreCode,
      row.contact,
      row.amount,
      row.paymentMode,
      row.reference,
      row.franchiseNote,
      row.adminNote,
      row.date,
    ]),
  ];

  const csv = lines
    .map((line) =>
      line
        .map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`)
        .join(",")
    )
    .join("\n");

  downloadBlob(csv, fileName, "text/csv;charset=utf-8;");
}

export function exportLoadMoneyExcel({ rows, fileName, title, dateField = "updated_at", dateLabel = "Date" }) {
  const exportRows = toExportRows(rows, dateField);
  const bodyRows = exportRows
    .map(
      (row) =>
        `<tr>
          <td>${row.sl}</td>
          <td>${escapeHtml(row.franchise)}</td>
          <td>${escapeHtml(row.centreCode)}</td>
          <td>${escapeHtml(row.contact)}</td>
          <td>${escapeHtml(row.amount)}</td>
          <td>${escapeHtml(row.paymentMode)}</td>
          <td>${escapeHtml(row.reference)}</td>
          <td>${escapeHtml(row.franchiseNote)}</td>
          <td>${escapeHtml(row.adminNote)}</td>
          <td>${escapeHtml(row.date)}</td>
        </tr>`
    )
    .join("");

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 12px; text-align: left; }
          th { background: #f8fafc; }
          h2 { margin: 0 0 14px; }
        </style>
      </head>
      <body>
        <h2>${escapeHtml(title)}</h2>
        <table>
          <thead>
            <tr>
              <th>Sl No</th>
              <th>Franchise</th>
              <th>Centre Code</th>
              <th>Contact</th>
              <th>Amount (Rs)</th>
              <th>Payment Mode</th>
              <th>Reference</th>
              <th>Franchise Note</th>
              <th>Admin Note</th>
              <th>${escapeHtml(dateLabel)}</th>
            </tr>
          </thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
    </html>
  `;

  downloadBlob(html, fileName, "application/vnd.ms-excel;charset=utf-8;");
}

export function exportLoadMoneyPdf({ rows, fileName, title, dateField = "updated_at", dateLabel = "Date" }) {
  const exportRows = toExportRows(rows, dateField);
  const wrapper = document.createElement("div");
  wrapper.style.padding = "16px";
  wrapper.style.background = "#fff";
  wrapper.style.color = "#0f172a";
  wrapper.style.fontFamily = "Arial, sans-serif";

  const bodyRows = exportRows
    .map(
      (row) =>
        `<tr>
          <td>${row.sl}</td>
          <td>${escapeHtml(row.franchise)}</td>
          <td>${escapeHtml(row.centreCode)}</td>
          <td>${escapeHtml(row.contact)}</td>
          <td>${escapeHtml(row.amount)}</td>
          <td>${escapeHtml(row.paymentMode)}</td>
          <td>${escapeHtml(row.reference)}</td>
          <td>${escapeHtml(row.franchiseNote)}</td>
          <td>${escapeHtml(row.adminNote)}</td>
          <td>${escapeHtml(row.date)}</td>
        </tr>`
    )
    .join("");

  wrapper.innerHTML = `
    <h2 style="margin:0 0 12px;">${escapeHtml(title)}</h2>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Sl No</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Franchise</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Centre Code</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Contact</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Amount (Rs)</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Payment Mode</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Reference</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Franchise Note</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">Admin Note</th>
          <th style="border:1px solid #cbd5e1;padding:6px;text-align:left;font-size:11px;background:#f8fafc;">${escapeHtml(dateLabel)}</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>
  `;

  document.body.appendChild(wrapper);
  html2pdf()
    .set({
      margin: 0.3,
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    })
    .from(wrapper)
    .save()
    .finally(() => {
      if (wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }
    });
}
